import { apiFetch } from "./client";

export type MaterialKind = "LOGO" | "BROCHURE" | "IMAGE" | "VIDEO" | "OTHER";

export type MaterialCategory = {
  id: string;
  slug: string;
  displayOrder: number;
  name: string;
  translations: Record<string, string>;
};

export type MaterialView = {
  id: string;
  categoryId: string;
  categorySlug: string;
  kind: MaterialKind;
  filename: string;
  sizeBytes: number;
  contentType: string;
  visibleToRoles: string[];
  translations: Record<string, { title: string; description: string | null }>;
  uploadedAt: string;
};

const q = (params: Record<string, string | undefined>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && usp.set(k, v));
  const s = usp.toString();
  return s ? `?${s}` : "";
};

export const materialsApi = {
  categories: (locale = "en") =>
    apiFetch<MaterialCategory[]>(`/materials/categories${q({ locale })}`),

  list: (categoryId?: string) =>
    apiFetch<MaterialView[]>(`/materials${q({ categoryId })}`),

  presign: (id: string) =>
    apiFetch<{ url: string; ttlSeconds: string }>(`/materials/${id}/download-url`),

  // Admin
  createCategory: (body: {
    slug: string;
    displayOrder?: number;
    translations: Record<string, string>;
  }) => apiFetch<MaterialCategory>("/admin/materials/categories", { method: "POST", body }),

  upload: async (
    metadata: {
      categoryId: string;
      kind: MaterialKind;
      visibleToRoles: string[];
      translations: Record<string, { title: string; description: string | null }>;
    },
    file: File,
  ): Promise<MaterialView> => {
    const fd = new FormData();
    fd.append(
      "metadata",
      new Blob([JSON.stringify(metadata)], { type: "application/json" }),
    );
    fd.append("file", file);
    const res = await fetch("/api/admin/materials/upload", {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    if (!res.ok) {
      let body: { code?: string; message?: string } = {};
      try { body = (await res.json()) as { code?: string; message?: string }; } catch { /* ignore */ }
      throw {
        status: res.status,
        code: body.code ?? `HTTP_${res.status}`,
        message: body.message ?? res.statusText,
      };
    }
    return (await res.json()) as MaterialView;
  },

  delete: (id: string) =>
    apiFetch<void>(`/admin/materials/${id}`, { method: "DELETE" }),
};
